import { faUser } from "@fortawesome/free-regular-svg-icons";
import {
	faArrowRightFromBracket,
	faChevronDown,
	faUserPen,
	faXmark
} from "@fortawesome/free-solid-svg-icons";
import React, { useContext, useMemo } from "react";
import { Button, Container, Dropdown, Nav, Navbar, Offcanvas } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { AppRulesContext } from "../../App";
import { appConstants } from "../../config/app-constants";
import { IAppRoutes } from "../../config/interfaces";
import { useAppSelector } from "../../redux/Store";
import { loggedInUserSelector } from "../../redux/slice/LoggedInUserSlice";
import { UtilService } from "../../services";
import FASvgIcon from "../FASvgIcon";

type Props = {
	sidebarOpened: boolean;
	isSmallView: boolean;
	theme: string;
	toggleSidebar: () => void;
};

const DBHeader: React.FC<Props> = (props: Props) => {
	const { theme, sidebarOpened, toggleSidebar, isSmallView } = props;

	const navigate = useNavigate();

	const { rulesSvc } = useContext(AppRulesContext);

	const { loggedInUser } = useAppSelector(loggedInUserSelector);

	const handleSignOut = () => {
		UtilService.handleLogoutOnUnauthorized();
	};

	const appRoutes: IAppRoutes[] = useMemo(() => {
		return [
			{
				groupTitle: "Fillings",
				label: "Fillings",
				icon: "",
				parentLink: "/itr-filings",
				appRouteLink: "/itr-filings",
				permissionConfig: rulesSvc.eFilingList,
				subItems: [],
				allowedTo: ["admin", "ca", "user"]
			},
			{
				groupTitle: "Users",
				label: "Users",
				icon: "",
				parentLink: "/users",
				appRouteLink: "/users",
				permissionConfig: rulesSvc.usersList,
				subItems: [],
				allowedTo: ["admin"]
			},
			{
				groupTitle: "Tax Experts",
				label: "Tax Experts",
				icon: "",
				parentLink: "/tax-experts",
				appRouteLink: "/tax-experts",
				permissionConfig: rulesSvc.usersList,
				subItems: [],
				allowedTo: ["admin"]
			},
			{
				groupTitle: "Pricing Plans",
				label: "Pricing Plans",
				icon: "",
				parentLink: "/plans",
				appRouteLink: "/plans",
				permissionConfig: rulesSvc.plansList,
				subItems: [
					{
						groupTitle: "Self filing plans",
						label: "Self filing plans",
						icon: "",
						parentLink: "/plans/self-filings",
						appRouteLink: "/plans/self-filings",
						permissionConfig: rulesSvc.plansList,
						allowedTo: ["admin"]
					},
					{
						groupTitle: "Assisted filing plans",
						label: "Assisted filing plans",
						icon: "",
						parentLink: "/plans/assisted-filings",
						appRouteLink: "/plans/assisted-filings",
						permissionConfig: rulesSvc.plansList,
						allowedTo: ["admin"]
					},
					{
						groupTitle: "Pricing page settings",
						label: "Pricing page settings",
						icon: "",
						parentLink: "/plans/footer-settings",
						appRouteLink: "/plans/footer-settings",
						permissionConfig: rulesSvc.plansList,
						allowedTo: ["admin"]
					}
				],
				allowedTo: ["admin"]
			},
			{
				groupTitle: "Blog",
				label: "Blog",
				icon: "",
				parentLink: "/blog",
				appRouteLink: "/blog",
				permissionConfig: rulesSvc.usersList,
				subItems: [],
				allowedTo: ["admin"]
			},
			{
				groupTitle: "Settings",
				label: "Settings",
				icon: "",
				parentLink: "/settings",
				appRouteLink: "/settings",
				permissionConfig: rulesSvc.globalSettingList,
				subItems: [],
				allowedTo: ["admin"]
			}
		];
	}, []);

	return (
		<>
			<Navbar
				className="main-header"
				expanded={isSmallView && sidebarOpened}
				expand="lg"
				bg={theme}
				variant={theme}
			>
				<Container fluid>
					{loggedInUser?.role ? (
						<Navbar.Toggle
							aria-controls="responsive-navbar-nav"
							onClick={() => toggleSidebar()}
							className="me-auto"
						/>
					) : null}

					<Navbar.Brand
						role="button"
						onClick={(e) => {
							toggleSidebar();
							e.preventDefault();
							navigate("/");
						}}
					>
						<img
							className="img-fluid d-none d-sm-block"
							src={`${appConstants.basePath}/assets/images/logo-dark.svg`}
							alt="dark logo"
						/>
						<img
							className="img-fluid d-block d-sm-none"
							src={`${appConstants.basePath}/assets/images/logo-small.svg`}
							alt="dark logo"
						/>
					</Navbar.Brand>

					{loggedInUser?.role ? (
						<Navbar.Offcanvas
							id="responsive-navbar-nav"
							aria-labelledby="offcanvasNavbarLabel-expand"
							placement="start"
							className="menu-offcanvas"
						>
							<Offcanvas.Header>
								<Offcanvas.Title id="offcanvasNavbarLabel-expand">
									<img
										className="img-fluid navbar-logo"
										src={`${appConstants.basePath}/assets/images/logo-dark.svg`}
										alt="dark logo"
									/>
								</Offcanvas.Title>
								<Button
									variant="primary"
									className="icon-only hide-offcanvas"
									onClick={() => toggleSidebar()}
								>
									<FASvgIcon icon={faXmark} />
								</Button>
							</Offcanvas.Header>
							<Offcanvas.Body>
								<Nav className="ms-auto">
									{appRoutes?.map((menuItem, index) =>
										rulesSvc.canAccess(menuItem.permissionConfig) &&
											menuItem.allowedTo.includes(loggedInUser?.role || "admin") ? (
											<React.Fragment key={index}>
												{menuItem?.subItems && menuItem?.subItems?.length > 0 ? (
													<Dropdown
														key={`menu-${index}`}
														className="navbar-item"
														id="collapsible-nav-dropdown"
													>
														<Dropdown.Toggle className="nav-link" variant="transparent">
															<span className="dropdown-title">{menuItem.groupTitle}</span>
															<FASvgIcon icon={faChevronDown} />
														</Dropdown.Toggle>
														<Dropdown.Menu align={"end"}>
															{menuItem?.subItems?.map((subItem: any, subIndex) =>
																rulesSvc.canAccess(subItem.permissionConfig) ? (
																	<Dropdown.Item
																		key={`sub-${index}-${subIndex}`}
																		onClick={(e) => {
																			e.preventDefault();
																			navigate(subItem.appRouteLink);
																			toggleSidebar();
																		}}
																	>
																		{subItem.label}
																	</Dropdown.Item>
																) : null
															)}
														</Dropdown.Menu>
													</Dropdown>
												) : (
													<Nav.Link
														href="#"
														onClick={(e) => {
															e.preventDefault();
															navigate(menuItem.appRouteLink);
															toggleSidebar();
														}}
													>
														{menuItem.label}
													</Nav.Link>
												)}
											</React.Fragment>
										) : null
									)}
								</Nav>
							</Offcanvas.Body>
						</Navbar.Offcanvas>
					) : null}

					<Navbar className="ms-auto">
						{/* <Nav>
							<div className="btns-wrap">
								<Button
									size="sm"
									type="button"
									variant="outline-greyDark"
									className="btn-w-160"
									onClick={() => handleCreateEmptyRecord()}
									disabled={isCreating}
								>
									{isCreating ? (
										<ButtonSpinner />
									) : (
										<>
											<FASvgIcon icon={faPlus} /> New Filing
										</>
									)}
								</Button>
								<Button
									size="sm"
									type="button"
									variant="secondary"
									className="btn-w-160 text-white"
                  style={{marginRight:'1.5rem'}}
									onClick={() => navigate("/itr-filings")}
								>
									View All Filings
								</Button>
							</div>
						</Nav> */}
						<Nav>
							<Dropdown className="navbar-item navbar-user-dropdown">
								<Dropdown.Toggle className="nav-link" variant="transparent">
									<div className="user-info-wrap">
										<div className="avatar">
											<FASvgIcon icon={faUser} />
										</div>
									</div>
									<FASvgIcon icon={faChevronDown} />
								</Dropdown.Toggle>

								<Dropdown.Menu align={"end"}>
									<Dropdown.ItemText className="text-capitalize">
										{loggedInUser?.fullName}
										<small className="d-block">{loggedInUser?.role}</small>
									</Dropdown.ItemText>
									<Dropdown.Divider />
									<Dropdown.Item eventKey="1" onClick={() => navigate("/profile")}>
										<FASvgIcon icon={faUserPen} />
										Profile
									</Dropdown.Item>
									<Dropdown.Divider />
									<Dropdown.Item eventKey="3" onClick={() => handleSignOut()}>
										<FASvgIcon icon={faArrowRightFromBracket} />
										Sign Out
									</Dropdown.Item>
								</Dropdown.Menu>
							</Dropdown>
						</Nav>
					</Navbar>
				</Container>
			</Navbar>
		</>
	);
};

export default DBHeader;
